// --- Web Components Implementation (using Liquid markup structure) ---

// Quantity Picker Web Component (no shadow DOM)
class QuantityPicker extends HTMLElement {
  constructor () {
    super()
    this.input = null
    this.incBtn = null
    this.decBtn = null
    this.max = null
    this.maxMsg = null
  }

  connectedCallback () {
    this.input = this.querySelector('.input-number')
    this.incBtn = this.querySelector('.input-number-increment')
    this.decBtn = this.querySelector('.input-number-decrement')
    this.max = parseInt(this.input.getAttribute('max')) || null
    this.maxMsg = this.querySelector('.max-msg') || this.createMaxMsg()
    this.incBtn.addEventListener('click', () => this.changeQuantity(1))
    this.decBtn.addEventListener('click', () => this.changeQuantity(-1))
    this.input.addEventListener('input', () => this.onInputChange())
    this.addMaxMsgHandler()
  }

  setMax (val) {
    this.max = val
    this.input.setAttribute('max', val)
    if (parseInt(this.input.value) > val) {
      this.input.value = val
      this.dispatchChange()
    }
  }

  changeQuantity (delta) {
    let val = parseInt(this.input.value) || 1
    val += delta
    if (val < 1) val = 1
    if (this.max && val > this.max) val = this.max
    this.input.value = val
    this.dispatchChange()
    this.checkMaxMsg()
  }

  onInputChange () {
    let val = parseInt(this.input.value) || 1
    if (this.max && val > this.max) val = this.max
    if (val < 1) val = 1
    this.input.value = val
    this.dispatchChange()
    this.checkMaxMsg()
  }

  dispatchChange () {
    this.dispatchEvent(
      new CustomEvent('quantity-change', {
        detail: { quantity: parseInt(this.input.value) },
        bubbles: true
      })
    )
  }

  createMaxMsg () {
    const msg = document.createElement('span')
    msg.classList.add('max-msg')
    msg.style.display = 'none'
    this.appendChild(msg)
    return msg
  }

  addMaxMsgHandler () {
    ;[this.incBtn, this.decBtn].forEach(btn => {
      btn.addEventListener('click', () => this.checkMaxMsg())
    })
  }

  checkMaxMsg () {
    if (this.max && parseInt(this.input.value) >= this.max) {
      this.maxMsg.innerText = `Max limit reached: ${this.max}`
      this.maxMsg.style.display = 'inline-block'
    } else {
      this.maxMsg.style.display = 'none'
    }
  }

  reset () {
    this.input.value = 1
    this.dispatchChange()
  }
}

if (!customElements.get('quantity-picker')) {
  // Define the custom element only if it doesn't already exist
  customElements.define('quantity-picker', QuantityPicker)
}

// Variant Picker Web Component (no shadow DOM)
class VariantPicker extends HTMLElement {
  constructor () {
    super()
    this.product = null
    this.moneyFormat = null
    this.form = null
    this.wrapper = null
    this.hiddenInput = null
  }

  async connectedCallback () {
    // Only activate if inside product-details or product-card
    this.wrapper =
      this.closest('product-details') || this.closest('product-card')
    if (!this.wrapper) return

    // Get product object from parent wrapper
    let productJson = this.wrapper.querySelector('.product-json')?.textContent
    try {
      this.product = JSON.parse(productJson)
    } catch (e) {
      this.product = productJson // fallback if not JSON
    }

    // If product is not an object, fetch product data
    if (typeof this.product !== 'object' || !this.product.variants) {
      const handle = typeof this.product === 'string' ? this.product : ''
      if (handle) {
        try {
          const res = await fetch(`${window.Shopify.routes.root}products/${handle}.js`)
          if (res.ok) {
            this.product = await res.json()
          } else {
            console.warn('Failed to fetch product data:', res.status)
            return
          }
        } catch (err) {
          console.warn('Error fetching product data:', err)
          return
        }
      } else {
        console.warn('Invalid product handle')
        return
      }
    }

    this.moneyFormat = this.dataset.moneyFormat
    this.form = this.closest('form')
    this.hiddenInput = this.form?.querySelector('input[name="id"]')
    if (!this.product || !this.moneyFormat || !this.form) {
      console.warn('VariantPicker requires product data and money format')
      return
    }

    if (this.product.variants?.length) {
      const defaultVariant = this.product.variants[0]
      if (this.hiddenInput) this.hiddenInput.value = defaultVariant.id
      this.updateUI(defaultVariant)
      this.handleVariantChange(defaultVariant)
    }

    this.querySelectorAll(
      'select.product-variant-dropdown,.product_options input[type=radio]'
    ).forEach(el => {
      el.addEventListener('change', e => {
        if (e.target.matches('select, input[type=radio]')) {
          this.handleVariantChange()
        }
      })
    })
    this.addKeyboardAccessibility()
  }

  handleVariantChange (variant) {
    const selectedOptions = this.getSelectedOptions()
    if (!this.product) return
    let matchedVariant
    if (variant) {
      matchedVariant = variant
    } else {
      matchedVariant = this.product.variants.find(variant => {
        return variant.options.every((opt, i) => selectedOptions[i] === opt)
      })
    }

    if (matchedVariant) {
      this.hiddenInput.value = matchedVariant.id
      this.dispatchEvent(
        new CustomEvent('variant-change', {
          detail: { variant: matchedVariant },
          bubbles: true
        })
      )
      this.updateUI(matchedVariant)
      // Slide the single-product-swiper to the first slide
      const swiperEl = this.wrapper.querySelector('.single-product-swiper')
      if (swiperEl && swiperEl.swiper) {
        swiperEl.swiper.slideTo(0)
      }
    }
  }

  getSelectedOptions () {
    const options = []
    this.querySelectorAll(
      'input[type=radio]:checked, select.product-variant-dropdown'
    ).forEach(el => {
      options.push(el.value)
    })
    return options
  }

  updateUI (variant) {
    // Update price, compare price, unit price, SKU, etc.
    this.form.querySelectorAll('.product-price').forEach(price => {
      price.textContent = formatMoney(variant.price, this.moneyFormat)
    })
    this.form.querySelectorAll('.product-compare-price').forEach(price => {
      variant.compare_at_price !== null
        ? (price.textContent = formatMoney(
            variant.compare_at_price,
            this.moneyFormat
          ))
        : (price.textContent = '')
    })
    this.form.querySelectorAll('.unit-price').forEach(price => {
      price.textContent = formatMoney(variant.unit_price, this.moneyFormat)
    })
    this.form
      .querySelectorAll('.productUnitPriceMeasurement')
      .forEach(price => {
        price.textContent = variant.unit_price_measurement.reference_value
      })
    this.form.querySelectorAll('.productReferenceUnit').forEach(price => {
      price.textContent = variant.unit_price_measurement.reference_unit
    })
    this.form.querySelectorAll('.product-sku').forEach(sku => {
      sku.textContent = variant.sku
    })
    // Sale tag
    const saleTags = this.form.querySelectorAll('.addSaleTag')
    saleTags.forEach(saleTag => {
      if (
        variant.compare_at_price > variant.price &&
        variant.inventory_quantity > 0
      ) {
        saleTag.textContent = 'Sale'
      } else {
        saleTag.textContent = 'Sold Out'
      }
    })

    // Pickup availability
    let pickupEl = this.wrapper.querySelector('pickup-availability')
    let pickupVariant = this.wrapper.querySelector(
      '.pickup-availability-variant'
    )
    if (pickupVariant) pickupVariant.innerHTML = variant.title
    if (pickupEl) pickupEl.setAttribute('data-variant-id', variant.id)
    // Variant image
    if (variant.featured_image) {
      const swiper = this.wrapper.querySelector('.single-product-swiper')
      if (swiper) {
        const firstSlide = swiper.querySelector('.swiper-slide')
        if (firstSlide) {
          const img = firstSlide.querySelector('img')
          if (img) {
            img.setAttribute('src', variant.featured_image.src)
            img.setAttribute('alt', variant.featured_image.alt)
            img.setAttribute('loading', 'lazy')
            img.removeAttribute('srcset')
          }
        }
      }else{
        let cardImg = this.wrapper.querySelector(".product__image")
        if(cardImg){
          cardImg.setAttribute("src",variant.featured_image.src)
          cardImg.setAttribute('alt', variant.featured_image.alt)
          cardImg.removeAttribute("srcset")
        }
      }
    }
    // Update URL
    if (typeof URLParse !== 'undefined') {
      let url = new URLParse(window.location.href, true)
      url.query.variant = variant.id
      window.history.replaceState(null, null, url.toString())
    }
    // Hide unavailable variants
    this.hideUnavailableVariants()
  }

  hideUnavailableVariants () {
    // This logic is adapted from the old VariantAvailability web component
    let availableVariants = this.form.querySelectorAll(
      '.product-variant-select option.available'
    )
    let newOption1 = [],
      newOption2 = [],
      newOption3 = []
    availableVariants.forEach(option => {
      let optionVal = option.value
      let newVariant = this.product.variants.find(
        variant => variant.id == optionVal
      )
      newOption1.push(newVariant.option1)
      newOption2.push(newVariant.option2)
      newOption3.push(newVariant.option3)
    })
    let option1 = this.querySelector(
      ".single_options > fieldset[data-option='1']"
    )
    let option2 = this.querySelector(
      ".single_options > fieldset[data-option='2']"
    )
    let option3 = this.querySelector(
      ".single_options > fieldset[data-option='3']"
    )
    if (option1) {
      let values1 = option1.querySelectorAll('.product-variant-option')
      values1.forEach(val => {
        newOption1.forEach(newVal => {
          if (val.querySelector("input[type='radio']").value == newVal) {
            val.classList.add('hasStock')
          }
        })
      })
    }
    if (option2) {
      let values2 = option2.querySelectorAll('.product-variant-option')
      values2.forEach(val => {
        newOption2.forEach(newVal => {
          if (val.querySelector("input[type='radio']").value == newVal) {
            val.classList.add('hasStock')
          }
        })
      })
    }
    if (option3) {
      let values3 = option3.querySelectorAll('.product-variant-option')
      values3.forEach(val => {
        newOption3.forEach(newVal => {
          if (val.querySelector("input[type='radio']").value == newVal) {
            val.classList.add('hasStock')
          }
        })
      })
    }
  }

  addKeyboardAccessibility () {
    this.querySelectorAll('.product-variant-option').forEach(option => {
      option.addEventListener('keyup', e => {
        if (e.key == 'Enter' || e.key == ' ') {
          option.querySelector('input').click()
        }
      })
    })
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker)
}

// Buy Buttons Web Component (no shadow DOM)
class BuyButtons extends HTMLElement {
  constructor () {
    super()
    this.variant = null
    this.quantity = 1
    this.form = null
  }

  connectedCallback () {
    // Only activate if inside product-details or product-card
    this.form = this.closest('form')
    const wrapper =
      this.closest('product-details') || this.closest('product-card')
    if (!wrapper) return
    this.addBtn = this.querySelector('.product-add')
    this.buyBtn = this.querySelector('.product-buy')
    this.addBtn?.addEventListener('click', e => this.addToCart(e))
    this.buyBtn?.addEventListener('click', e => this.buyNow(e))
    document.addEventListener('variant-change', e => this.onVariantChange(e))
    document.addEventListener('quantity-change', e => this.onQuantityChange(e))

    // Try to find the product JSON in the closest wrapper
    let product = null
    try {
      product = JSON.parse(wrapper.querySelector('.product-json').textContent)
    } catch (e) {
      product = null
    }
    if (product && product.variants) {
      const initialVariant = product.variants[0]
      if (initialVariant) {
        this.variant = initialVariant
        this.updateButtons()
        // Set max for quantity picker
        const quantityPicker = this.form.querySelector('quantity-picker')
        if (quantityPicker && this.variant) {
          quantityPicker.setMax(this.variant.inventory_quantity)
        }
      }
    }
  }

  onVariantChange (e) {
    this.variant = e.detail.variant
    this.updateButtons()
    const quantityPicker = this.form.querySelector('quantity-picker')
    if (quantityPicker && this.variant) {
      quantityPicker.setMax(this.variant.inventory_quantity)
      if (this.variant.available) {
        quantityPicker.reset()
      }
    }
  }

  onQuantityChange (e) {
    this.quantity = e.detail.quantity
  }

  updateButtons () {
    if (!this.variant) return
    if (this.variant.available) {
      this.addBtn.querySelector('.btn-text').textContent = 'Add to Cart'
      this.addBtn.disabled = false
      this.buyBtn.querySelector('.btn-text').textContent = 'Buy Now'
      this.buyBtn.disabled = false
      if (this.additionCheckout)
        this.additionCheckout.classList.remove('disabled')
    } else {
      this.addBtn.querySelector('.btn-text').textContent = 'Out of Stock'
      this.addBtn.disabled = true
      this.buyBtn.querySelector('.btn-text').textContent = 'Out of Stock'
      this.buyBtn.disabled = true
      if (this.additionCheckout) this.additionCheckout.classList.add('disabled')
    }
  }

  async addToCart (e) {
    e.preventDefault()
    if (!this.variant) return
    this.addBtn.querySelector('.spinner').style.display = 'inline-block'
    this.addBtn.querySelector('.btn-text').textContent = ''
    await fetch('/cart/add', {
      method: 'post',
      body: new URLSearchParams({
        id: this.variant.id,
        quantity: this.quantity
      })
    })
    this.addBtn.querySelector('.spinner').style.display = 'none'
    this.addBtn.querySelector('.btn-text').textContent = 'Added to Cart'
    setTimeout(() => {
      this.addBtn.querySelector('.btn-text').textContent = 'Add to Cart'
    }, 5000)
    const res = await fetch('/cart.json')
    const cart = await res.json()
    document.querySelectorAll('.cart-count').forEach(el => {
      el.textContent = cart.item_count
    })
  }

  buyNow (e) {
    e.preventDefault()
    if (!this.variant) return
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/checkout'
    form.style.display = 'none'
    const idInput = document.createElement('input')
    idInput.type = 'hidden'
    idInput.name = 'id'
    idInput.value = this.variant.id
    form.appendChild(idInput)
    const qtyInput = document.createElement('input')
    qtyInput.type = 'hidden'
    qtyInput.name = 'quantity'
    qtyInput.value = this.quantity
    form.appendChild(qtyInput)
    document.body.appendChild(form)
    form.submit()
  }
}

if (!customElements.get('buy-buttons')) {
  customElements.define('buy-buttons', BuyButtons)
}

// Product Details Web Component
class ProductDetails extends HTMLElement {
  constructor () {
    super()
    this.product = null
  }

  connectedCallback () {
    try {
      this.product = JSON.parse(this.querySelector('.product-json').textContent)
    } catch (e) {
      console.warn('Invalid product JSON:', e)
    }
    if (!this.product) return
    // Optionally, you can render product details here or delegate to child components
  }
}

if (!customElements.get('product-details')) {
  customElements.define('product-details', ProductDetails)
}

// Product Card Web Component
class ProductCard extends HTMLElement {
  constructor () {
    super()
    this.product = null
  }

  connectedCallback () {
    try {
      this.product = JSON.parse(this.querySelector('.product-json').textContent)
    } catch (e) {
      console.warn('Invalid product JSON:', e)
    }

    if (!this.product) return
    // Optionally, you can render product card here or delegate to child components
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard)
}
